import Logger from "../logger"
import ObjectUtil from "../util/ObjectUtil"
import TypeUtil from "../util/TypeUtil"
import IdentifierUtil from "../util/IdentifierUtil"
import PropertyUtil from "../util/PropertyUtil"
import { ServiceType } from "./ServiceType"
import ConfigEntity from "../workspace/config/entity/ConfigEntity"
import {
  InAppMessageAfterConditionDto,
  InAppMessageAlignmentDto,
  InAppMessageContextActionDto,
  InAppMessageContextButtonDto,
  InAppMessageContextCloseButtonDto,
  InAppMessageContextDto,
  InAppMessageContextImageAutoScrollDto,
  InAppMessageContextImageDto,
  InAppMessageContextMessageDto,
  InAppMessageContextTextContentDto,
  InAppMessageContextTextDto,
  InAppMessageDelayDto,
  InAppMessageDto,
  InAppMessageDurationCapDto,
  InAppMessageEvaluateContextDto,
  InAppMessageEventFrequencyCapDto,
  InAppMessageEventTriggerRuleDto,
  InAppMessageHtmlDto,
  InAppMessageIdentifierCapDto,
  InAppMessageLayoutDto,
  InAppMessagePositionalButtonDto,
  InAppMessageTargetContextDto,
  InAppMessageTargetRuleOverrideDto,
  InAppMessageTimetableDto,
  InAppMessageTimetableSlotDto
} from "../workspace/iam"
import {
  BucketDto,
  ContainerDto,
  ContainerGroupDto,
  ExecutionTargetUrlsDto,
  ExperimentDto,
  ParameterConfigurationDto,
  RemoteConfigParameterDto,
  RemoteConfigParameterValueDto,
  RemoteConfigTargetRuleDto,
  SegmentDto,
  SplitUrlConfigDto,
  TargetActionDto,
  TargetConditionDto,
  TargetDto,
  TargetKeyDto,
  TargetMatchDto,
  TargetRuleDto
} from "../workspace/dto"
import CollectionUtil from "../util/CollectionUtil"
import { PropertyOperation } from "../../../hackle/property/PropertyOperation"
import { DAY_OF_WEEK, DayOfWeek, TIME_UNIT, TimeUtil } from "../util/TimeUtil"
import { DefaultParameterConfig, EmptyParameterConfig, ParameterConfig } from "../config/ParameterConfig"
import { IAM_HIDE_EXPIRATION_MILLIS } from "../../../config"
import { HackleExperiment, HackleInAppMessage, HackleVariation } from "../../../public/model"
import { UserTargetEventDto, UserTargetEventPropertyDto, UserTargetStatDto } from "../user/dto"
import Entity from "./Entity"

export type Int = number
export type Long = number
export type List<T> = T[]
export type Double = number
export type UserId = string
export type VariationId = Long
export type VariationKey = string
export type ExperimentId = Long
export type ExperimentKey = Long
export type BucketId = Long
export type SegmentKey = string
export type ContainerId = Long
export type ParameterConfigurationId = Long
export type RemoteConfigParameterKey = string
export type InAppMessageKey = Long

const log = Logger.log

/**
 * An object that contains the decided config parameter value and the reason for the config parameter decision.
 */
export class RemoteConfigDecision {
  value: string | number | boolean
  reason: DecisionReason

  constructor(value: string | number | boolean, reason: DecisionReason) {
    this.value = value
    this.reason = reason
  }

  static of(value: string | number | boolean, reason: DecisionReason): RemoteConfigDecision {
    return new RemoteConfigDecision(value, reason)
  }
}

/**
 * An object that contains the decided variation and the reason for the decision.
 */
export class Decision implements ParameterConfig {
  constructor(
    readonly variation: VariationKey,
    readonly reason: DecisionReason,
    readonly config: ParameterConfig,
    readonly experiment: HackleExperiment | undefined
  ) {}

  static of(
    variation: VariationKey,
    reason: DecisionReason,
    config: ParameterConfig = new EmptyParameterConfig(),
    experiment: HackleExperiment | undefined = undefined
  ) {
    return new Decision(variation, reason, config, experiment)
  }

  get parameters(): Record<string, string | number | boolean> {
    return this.config.parameters
  }

  get(key: string, defaultValue: string): string
  get(key: string, defaultValue: number): number
  get(key: string, defaultValue: boolean): boolean
  get(key: string, defaultValue: string | number | boolean): string | number | boolean {
    switch (typeof defaultValue) {
      case "string":
        return this.config.get(key, defaultValue)
      case "number":
        return this.config.get(key, defaultValue)
      case "boolean":
        return this.config.get(key, defaultValue)
      default:
        return this.config.get(key, defaultValue)
    }
  }
}

/**
 * An object that contains the decided flag and the reason for the feature flag decision.
 */
export class FeatureFlagDecision implements ParameterConfig {
  constructor(
    readonly isOn: boolean,
    readonly reason: DecisionReason,
    readonly config: ParameterConfig,
    readonly experiment: HackleExperiment | undefined
  ) {}

  static on(
    reason: DecisionReason,
    config: ParameterConfig = new EmptyParameterConfig(),
    experiment: HackleExperiment | undefined = undefined
  ): FeatureFlagDecision {
    return new FeatureFlagDecision(true, reason, config, experiment)
  }

  static off(
    reason: DecisionReason,
    config: ParameterConfig = new EmptyParameterConfig(),
    experiment: HackleExperiment | undefined = undefined
  ): FeatureFlagDecision {
    return new FeatureFlagDecision(false, reason, config, experiment)
  }

  get parameters(): Record<string, string | number | boolean> {
    return this.config.parameters
  }

  get(key: string, defaultValue: string): string
  get(key: string, defaultValue: number): number
  get(key: string, defaultValue: boolean): boolean
  get(key: string, defaultValue: string | number | boolean): string | number | boolean {
    switch (typeof defaultValue) {
      case "string":
        return this.config.get(key, defaultValue)
      case "number":
        return this.config.get(key, defaultValue)
      case "boolean":
        return this.config.get(key, defaultValue)
      default:
        return this.config.get(key, defaultValue)
    }
  }
}

export interface Config {
  get(key: string, defaultValue: string): string

  get(key: string, defaultValue: number): number

  get(key: string, defaultValue: boolean): boolean
}

/**
 * An interface model for remote Config
 */
export interface HackleRemoteConfig extends Config {}

export class EmptyHackleRemoteConfig implements HackleRemoteConfig {
  get(key: string, defaultValue: string): string
  get(key: string, defaultValue: number): number
  get(key: string, defaultValue: boolean): boolean
  get(key: string, defaultValue: string | number | boolean): string | number | boolean {
    return defaultValue
  }
}

export class ParameterConfiguration implements ParameterConfig {
  constructor(public id: Long, private readonly config: ParameterConfig) {}

  get parameters(): Record<string, string | number | boolean> {
    return this.config.parameters
  }

  public static fromJson(dto: ParameterConfigurationDto): ParameterConfiguration {
    return new ParameterConfiguration(
      dto.id,
      new DefaultParameterConfig(CollectionUtil.associateRecord(dto.parameters, (it) => [it.key, it.value]))
    )
  }

  get(key: string, defaultValue: string): string
  get(key: string, defaultValue: number): number
  get(key: string, defaultValue: boolean): boolean
  get(key: string, defaultValue: string | number | boolean): string | number | boolean {
    switch (typeof defaultValue) {
      case "string":
        return this.config.get(key, defaultValue)
      case "number":
        return this.config.get(key, defaultValue)
      case "boolean":
        return this.config.get(key, defaultValue)
      default:
        return defaultValue
    }
  }
}

/**
 * Describes the reason for the [Variation] decision.
 */
const DECISION_REASON = {
  SDK_NOT_READY: "SDK_NOT_READY",
  EXCEPTION: "EXCEPTION",
  INVALID_INPUT: "INVALID_INPUT",
  UNSUPPORTED_PLATFORM: "UNSUPPORTED_PLATFORM",
  TYPE_MISMATCH: "TYPE_MISMATCH",

  EXPERIMENT_NOT_FOUND: "EXPERIMENT_NOT_FOUND",
  EXPERIMENT_DRAFT: "EXPERIMENT_DRAFT",
  EXPERIMENT_PAUSED: "EXPERIMENT_PAUSED",
  EXPERIMENT_COMPLETED: "EXPERIMENT_COMPLETED",
  OVERRIDDEN: "OVERRIDDEN",
  TRAFFIC_NOT_ALLOCATED: "TRAFFIC_NOT_ALLOCATED",
  NOT_IN_MUTUAL_EXCLUSION_EXPERIMENT: "NOT_IN_MUTUAL_EXCLUSION_EXPERIMENT",
  IDENTIFIER_NOT_FOUND: "IDENTIFIER_NOT_FOUND",
  VARIATION_DROPPED: "VARIATION_DROPPED",
  TRAFFIC_ALLOCATED: "TRAFFIC_ALLOCATED",
  NOT_IN_EXPERIMENT_TARGET: "NOT_IN_EXPERIMENT_TARGET",

  FEATURE_FLAG_NOT_FOUND: "FEATURE_FLAG_NOT_FOUND",
  FEATURE_FLAG_INACTIVE: "FEATURE_FLAG_INACTIVE",
  INDIVIDUAL_TARGET_MATCH: "INDIVIDUAL_TARGET_MATCH",
  TARGET_RULE_MATCH: "TARGET_RULE_MATCH",
  DEFAULT_RULE: "DEFAULT_RULE",

  REMOTE_CONFIG_PARAMETER_NOT_FOUND: "REMOTE_CONFIG_PARAMETER_NOT_FOUND",

  IN_APP_MESSAGE_NOT_FOUND: "IN_APP_MESSAGE_NOT_FOUND",
  IN_APP_MESSAGE_DRAFT: "IN_APP_MESSAGE_DRAFT",
  IN_APP_MESSAGE_PAUSED: "IN_APP_MESSAGE_PAUSED",
  IN_APP_MESSAGE_HIDDEN: "IN_APP_MESSAGE_HIDDEN",
  IN_APP_MESSAGE_TARGET: "IN_APP_MESSAGE_TARGET",
  NOT_IN_IN_APP_MESSAGE_PERIOD: "NOT_IN_IN_APP_MESSAGE_PERIOD",
  NOT_IN_IN_APP_MESSAGE_TIMETABLE: "NOT_IN_IN_APP_MESSAGE_TIMETABLE",
  NOT_IN_IN_APP_MESSAGE_TARGET: "NOT_IN_IN_APP_MESSAGE_TARGET",
  IN_APP_MESSAGE_FREQUENCY_CAPPED: "IN_APP_MESSAGE_FREQUENCY_CAPPED",
  EXPERIMENT_CONTROL_GROUP: "EXPERIMENT_CONTROL_GROUP",

  UNKNOWN: "UNKNOWN"
} as const

export type DecisionReason = typeof DECISION_REASON[keyof typeof DECISION_REASON]

const DECISION_REASON_VALUES = new Set<string>(Object.keys(DECISION_REASON))

export const DecisionReason = {
  ...DECISION_REASON,

  // Server responses can carry reasons newer than this SDK knows; they degrade to UNKNOWN.
  from(reason: string): DecisionReason {
    return DECISION_REASON_VALUES.has(reason) ? (reason as DecisionReason) : DECISION_REASON.UNKNOWN
  }
}

export const EXPERIMENT_IMPLEMENTATION_TYPE = ["DEFAULT", "SPLIT_URL", "IN_APP_MESSAGE"] as const

export type ExperimentType = "AB_TEST" | "FEATURE_FLAG"
export type ExperimentStatus = "DRAFT" | "RUNNING" | "PAUSED" | "COMPLETED"
export type ExperimentImplementationType = typeof EXPERIMENT_IMPLEMENTATION_TYPE[number]

/**
 * Identity/metadata view of an experiment. Implemented by the local config model
 * (`ExperimentConfig`) and, in remote evaluation, by the server-resolved result.
 */
export interface Experiment extends Entity, HackleExperiment {
  type: ExperimentType
  status: ExperimentStatus
  // Server-assigned priority. Carried on both the local config and the remote result.
  order: Long
  executionVersion: Long
  // Browser-only split-url extension carried on both the local config and the remote result.
  targetUrls: SplitUrlTargets | undefined
}

export abstract class AbstractExperiment implements Experiment {
  abstract readonly id: number
  abstract readonly key: number
  abstract readonly version: number
  abstract readonly type: ExperimentType
  abstract readonly status: ExperimentStatus
  abstract readonly order: Long
  abstract readonly executionVersion: Long
  abstract readonly targetUrls: SplitUrlTargets | undefined

  get serviceType(): ServiceType {
    switch (this.type) {
      case "AB_TEST":
        return "AB_TEST"
      case "FEATURE_FLAG":
        return "FEATURE_FLAG"
      default:
        throw new Error(`Unsupported experiment type [${this.type}]`)
    }
  }
}

export function parseExperimentStatusOrNull(executionStatus: string): ExperimentStatus | undefined {
  switch (executionStatus) {
    case "READY":
      return "DRAFT"
    case "RUNNING":
      return "RUNNING"
    case "PAUSED":
      return "PAUSED"
    case "STOPPED":
      return "COMPLETED"
    default:
      log.debug(`Unsupported status [${executionStatus}]`)
      return undefined
  }
}

export class ExperimentConfig extends AbstractExperiment implements ConfigEntity {
  public static fromJson(
    type: ExperimentType,
    dto: ExperimentDto,
    configs: ParameterConfiguration[]
  ): ExperimentConfig | undefined {
    const experimentStatus = parseExperimentStatusOrNull(dto.execution.status)
    const variations = dto.variations.map(
      (it) =>
        new Variation(
          it.id,
          it.key,
          it.status === "DROPPED",
          configs.find((c) => c.id === it.parameterConfigurationId)
        )
    )
    const userOverrides = CollectionUtil.associate(dto.execution.userOverrides, (it) => [it.userId, it.variationId])
    const segmentOverrides = CollectionUtil.mapNotNullOrUndefined(dto.execution.segmentOverrides, (it) =>
      TargetRule.fromJson(it, TargetingType.IDENTIFIER)
    )
    const targetAudiences = CollectionUtil.mapNotNullOrUndefined(dto.execution.targetAudiences, (it) =>
      Target.fromJson(it, TargetingType.PROPERTY)
    )
    const targetUrls = SplitUrlTargets.fromJson(dto.execution.targetUrls)
    const implementationType = ObjectUtil.parseOrNull(EXPERIMENT_IMPLEMENTATION_TYPE, dto.implementationType)
    const targetRules = CollectionUtil.mapNotNullOrUndefined(dto.execution.targetRules, (it) =>
      TargetRule.fromJson(it, TargetingType.PROPERTY)
    )
    const defaultRule = TargetAction.fromJson(dto.execution.defaultRule)
    return (
      experimentStatus &&
      implementationType &&
      defaultRule &&
      new ExperimentConfig(
        dto.id,
        dto.key,
        type,
        dto.identifierType,
        experimentStatus,
        dto.order,
        dto.version,
        dto.execution.version,
        variations,
        userOverrides,
        segmentOverrides,
        targetAudiences,
        targetUrls,
        targetRules,
        defaultRule,
        dto.containerId,
        implementationType,
        dto.winnerVariationId
      )
    )
  }

  constructor(
    public readonly id: ExperimentId,
    public readonly key: ExperimentKey,
    public readonly type: ExperimentType,
    public readonly identifierType: string,
    public readonly status: ExperimentStatus,
    public readonly order: Long,
    public readonly version: Long,
    public readonly executionVersion: Long,
    public readonly variations: Variation[],
    public readonly userOverrides: Map<UserId, VariationId>,
    public readonly segmentOverrides: TargetRule[],
    public readonly targetAudiences: Target[],
    public readonly targetUrls: SplitUrlTargets | undefined,
    public readonly targetRules: TargetRule[],
    public readonly defaultRule: TargetAction,
    public readonly containerId: Long | undefined,
    public readonly implementationType: ExperimentImplementationType,
    private readonly winnerVariationId: VariationId | undefined
  ) {
    super()
  }

  _winnerVariationOrNull(): Variation | undefined {
    if (this.winnerVariationId) {
      return this._getVariationByIdOrNull(this.winnerVariationId)
    }

    return undefined
  }

  _getVariationByIdOrNull(variationId: VariationId): Variation | undefined {
    return this.variations.find((it) => it.id === variationId)
  }

  _getVariationByKeyOrNull(variationKey: VariationKey): Variation | undefined {
    return this.variations.find((it) => it.key === variationKey)
  }
}

export class Variation implements HackleVariation {
  constructor(
    readonly id: VariationId,
    readonly key: VariationKey,
    readonly isDropped: boolean,
    readonly config: ParameterConfiguration | undefined
  ) {}

  get parameters(): Record<string, string | number | boolean> {
    return this.config?.parameters ?? {}
  }
}

export class Bucket {
  id: number
  seed: number
  slotSize: number
  slots: Slot[]

  constructor(id: number, seed: number, slotSize: number, slots: Slot[]) {
    this.id = id
    this.seed = seed
    this.slotSize = slotSize
    this.slots = slots
  }

  public static fromJson(dto: BucketDto): Bucket {
    return new Bucket(
      dto.id,
      dto.seed,
      dto.slotSize,
      dto.slots.map(
        ({ startInclusive, endExclusive, variationId }) => new Slot(startInclusive, endExclusive, variationId)
      )
    )
  }
}

export class Slot {
  startInclusive: number
  endExclusive: number
  variationId: VariationId

  constructor(startInclusive: number, endExclusive: number, variationId: VariationId) {
    this.startInclusive = startInclusive
    this.endExclusive = endExclusive
    this.variationId = variationId
  }

  contains(slotNumber: number): boolean {
    return this.startInclusive <= slotNumber && slotNumber < this.endExclusive
  }
}

export interface HackleEvent<T = string> {
  key: string
  value?: number
  properties?: Properties<T>
}

export interface HackleUser {
  identifiers: Identifiers
  properties: Properties
  hackleProperties: Properties
  cohorts?: Cohort[]
  targetEvents?: UserTargetEvent[]
}

// An empty user for lookups that are not user-scoped (e.g. workspace config introspection).
export function emptyHackleUser(): HackleUser {
  return { identifiers: {}, properties: {}, hackleProperties: {} }
}

export class IdentifierType {
  static ID = "$id"
  static USER = "$userId"
  static DEVICE = "$deviceId"
  static SESSION = "$sessionId"
  static HACKLE_DEVICE = "$hackleDeviceId"
}

export interface User {
  id?: string
  userId?: string
  deviceId?: string
  identifiers?: Identifiers
  properties?: Properties
}

export interface Identifier {
  readonly type: string
  readonly value: string
}

export interface Identifiers {
  [key: string]: string
}

export const resolveIdentifiers = (user: User): Identifiers => {
  const builder = new IdentifiersBuilder()
  builder.addIdentifiers(user.identifiers || {})
  builder.add(IdentifierType.ID, user.id)
  builder.add(IdentifierType.DEVICE, user.deviceId)

  if (ObjectUtil.isNotNullOrUndefined(user.userId)) {
    builder.add(IdentifierType.USER, user.userId)
  }

  return builder.build()
}

export class IdentifiersBuilder {
  identifiers: Identifiers = {}

  addIdentifiers(identifiers: Identifiers): IdentifiersBuilder {
    for (const identifierType in identifiers) {
      this.add(identifierType, identifiers[identifierType])
    }
    return this
  }

  add(type: string, value: any): IdentifiersBuilder {
    const sanitizeIdentifierValue = IdentifierUtil.sanitizeValue(value)
    if (IdentifierUtil.isValidType(type) && sanitizeIdentifierValue) {
      this.identifiers[type] = value
    } else {
      log.warn(`Invalid user identifier [type=${type}, value=${value}]`)
    }
    return this
  }

  build(): Identifiers {
    return this.identifiers
  }
}

export type PropertyValue = string | boolean | number | Array<string | number> | null | undefined

const isValidUser = (user: User): boolean => {
  return Boolean(
    user.id || user.userId || user.deviceId || (user.identifiers && Object.keys(user.identifiers).length > 0)
  )
}

export const sanitizeUser = (user: any): User | null => {
  const sanitized: User = {}

  sanitized.id = IdentifierUtil.sanitizeValue(user.id) ?? undefined

  sanitized.userId = IdentifierUtil.sanitizeValue(user.userId) ?? undefined

  sanitized.deviceId = IdentifierUtil.sanitizeValue(user.deviceId) ?? undefined

  if (typeof user.properties === "object") {
    sanitized.properties = PropertyUtil.sanitize(user.properties)
  }

  if (typeof user.identifiers === "object") {
    sanitized.identifiers = IdentifierUtil.sanitize(user.identifiers)
  }

  if (isValidUser(sanitized)) {
    return sanitized
  } else {
    return null
  }
}

export interface Properties<T = string | PropertyOperation> {
  [key: string | PropertyOperation]: T extends PropertyOperation ? Properties<string> : PropertyValue
}

export class Target {
  public static fromJson(dto: TargetDto, targetingType: TargetingType): Target | undefined {
    const conditions = CollectionUtil.mapNotNullOrUndefined(dto.conditions, (it) =>
      TargetCondition.fromJson(it, targetingType)
    )

    if (conditions.length === 0) return undefined

    return new Target(conditions)
  }

  conditions: TargetCondition[]

  constructor(conditions: TargetCondition[]) {
    this.conditions = conditions
  }

  public toJson(): TargetDto {
    return {
      conditions: this.conditions.map((condition) => condition.toJson())
    }
  }
}

export class TargetCondition {
  public static fromJson(dto: TargetConditionDto, targetingType: TargetingType): TargetCondition | undefined {
    const key = TargetKey.fromJson(dto.key)
    if (!key) {
      return undefined
    }
    if (!targetingType.supports(key.type)) {
      return undefined
    }
    const match = TargetMatch.fromJson(dto.match)
    return match && new TargetCondition(key, match)
  }

  key: TargetKey
  match: TargetMatch

  constructor(key: TargetKey, match: TargetMatch) {
    this.key = key
    this.match = match
  }

  public toJson(): TargetConditionDto {
    return {
      key: this.key.toJson(),
      match: this.match.toJson()
    }
  }
}

export class TargetKey {
  public static fromJson(dto: TargetKeyDto): TargetKey | undefined {
    const keyType = ObjectUtil.parseOrNull(TARGET_KEY_TYPES, dto.type)
    return keyType && new TargetKey(keyType, dto.name)
  }

  type: TargetKeyType
  name: string

  constructor(type: TargetKeyType, name: string) {
    this.type = type
    this.name = name
  }

  public toJson(): TargetKeyDto {
    return {
      type: this.type,
      name: this.name
    }
  }
}

export class TargetMatch {
  public static fromJson(dto: TargetMatchDto): TargetMatch | undefined {
    const matchType = ObjectUtil.parseOrNull(MATCH_TYPES, dto.type)
    const operator = ObjectUtil.parseOrNull(MATCH_OPERATORS, dto.operator)
    const valueType = ObjectUtil.parseOrNull(MATCH_VALUE_TYPES, dto.valueType)
    return matchType && operator && valueType && new TargetMatch(matchType, operator, valueType, dto.values)
  }

  type: MatchType
  operator: MatchOperator
  valueType: MatchValueType
  values: any[]

  constructor(type: MatchType, operator: MatchOperator, valueType: MatchValueType, values: any[]) {
    this.type = type
    this.operator = operator
    this.valueType = valueType
    this.values = values
  }

  public toJson(): TargetMatchDto {
    return {
      operator: this.operator,
      type: this.type,
      valueType: this.valueType,
      values: this.values
    }
  }
}

export class TargetAction {
  public static fromJson(dto: TargetActionDto): TargetAction | undefined {
    const type = ObjectUtil.parseOrNull(TARGET_ACTION_TYPES, dto.type)
    return type && new TargetAction(type, dto.variationId, dto.bucketId)
  }

  type: TargetActionType
  variationId: number | undefined
  bucketId: number | undefined

  constructor(type: TargetActionType, variationId: number | undefined, bucketId: number | undefined) {
    this.type = type
    this.variationId = variationId
    this.bucketId = bucketId
  }
}

export class TargetRule {
  public static fromJson(dto: TargetRuleDto, targetingType: TargetingType): TargetRule | undefined {
    const target = Target.fromJson(dto.target, targetingType)
    const action = TargetAction.fromJson(dto.action)
    return target && action && new TargetRule(target, action)
  }

  target: Target
  action: TargetAction

  constructor(target: Target, action: TargetAction) {
    this.target = target
    this.action = action
  }
}

export class Segment {
  public static fromJson(dto: SegmentDto): Segment | undefined {
    const segmentType = ObjectUtil.parseOrNull(SEGMENT_TYPES, dto.type)
    return (
      segmentType &&
      new Segment(
        dto.id,
        dto.key,
        segmentType,
        CollectionUtil.mapNotNullOrUndefined(dto.targets, (it) => Target.fromJson(it, TargetingType.SEGMENT))
      )
    )
  }

  id: Long
  key: SegmentKey
  type: SegmentType
  targets: Target[]

  constructor(id: Long, key: SegmentKey, type: SegmentType, targets: Target[]) {
    this.id = id
    this.key = key
    this.type = type
    this.targets = targets
  }
}

export class Container {
  id: Long
  bucketId: Long
  groups: ContainerGroup[]

  constructor(id: Long, bucketId: Long, groups: ContainerGroup[]) {
    this.id = id
    this.bucketId = bucketId
    this.groups = groups
  }

  public static fromJson(dto: ContainerDto): Container {
    return new Container(
      dto.id,
      dto.bucketId,
      dto.groups.map((it) => ContainerGroup.fromJson(it))
    )
  }

  getGroupOrNull(containerGroupId: Long): ContainerGroup | undefined {
    return this.groups.find((it) => it.id === containerGroupId)
  }
}

export class ContainerGroup {
  public static fromJson(dto: ContainerGroupDto): ContainerGroup {
    return new ContainerGroup(dto.id, dto.experiments)
  }

  id: Long
  experiments: Long[]

  constructor(id: Long, experiments: Long[]) {
    this.id = id
    this.experiments = experiments
  }
}

export interface RemoteConfigParameter extends Entity {
  key: string
  type: MatchValueType
}

export abstract class AbstractRemoteConfigParameter implements RemoteConfigParameter {
  abstract readonly id: Long
  abstract readonly key: string
  abstract readonly type: MatchValueType

  get serviceType(): ServiceType {
    return "REMOTE_CONFIG"
  }
}

export class RemoteConfigParameterConfig extends AbstractRemoteConfigParameter implements ConfigEntity {
  constructor(
    readonly id: Long,
    readonly key: string,
    readonly type: MatchValueType,
    readonly identifierType: string,
    readonly targetRules: RemoteConfigTargetRule[],
    readonly defaultValue: RemoteConfigParameterValue
  ) {
    super()
  }

  public static fromJson(dto: RemoteConfigParameterDto): RemoteConfigParameterConfig | undefined {
    const remoteConfigParameterType = ObjectUtil.parseOrNull(MATCH_VALUE_TYPES, dto.type)
    const targetRules = CollectionUtil.mapNotNullOrUndefined(dto.targetRules, (it) =>
      RemoteConfigTargetRule.fromJson(it, TargetingType.PROPERTY)
    )
    const defaultValue = RemoteConfigParameterValue.fromJson(dto.defaultValue)
    return (
      remoteConfigParameterType &&
      new RemoteConfigParameterConfig(
        dto.id,
        dto.key,
        remoteConfigParameterType,
        dto.identifierType,
        targetRules,
        defaultValue
      )
    )
  }
}

export class RemoteConfigTargetRule {
  public static fromJson(
    dto: RemoteConfigTargetRuleDto,
    targetingType: TargetingType
  ): RemoteConfigTargetRule | undefined {
    const target = Target.fromJson(dto.target, targetingType)
    return (
      target &&
      new RemoteConfigTargetRule(
        dto.key,
        dto.name,
        target,
        dto.bucketId,
        RemoteConfigParameterValue.fromJson(dto.value)
      )
    )
  }

  key: string
  name: string
  target: Target
  bucketId: Long
  value: RemoteConfigParameterValue

  constructor(key: string, name: string, target: Target, bucketId: Long, value: RemoteConfigParameterValue) {
    this.key = key
    this.name = name
    this.target = target
    this.bucketId = bucketId
    this.value = value
  }
}

export class RemoteConfigParameterValue {
  public static fromJson(dto: RemoteConfigParameterValueDto): RemoteConfigParameterValue {
    return new RemoteConfigParameterValue(dto.id, dto.value)
  }

  id: Long
  rawValue: any

  constructor(id: Long, rawValue: any) {
    this.id = id
    this.rawValue = rawValue
  }
}

function compareValues(a: number, b: number): number {
  return a - b
}

export class Version {
  readonly coreVersion: CoreVersion
  readonly prerelease: MetaVersion
  readonly build: MetaVersion

  constructor(coreVersion: CoreVersion, prerelease: MetaVersion, build: MetaVersion) {
    this.coreVersion = coreVersion
    this.prerelease = prerelease
    this.build = build
  }

  static regExp =
    /^(0|[1-9]\d*)(?:\.(0|[1-9]\d*))?(?:\.(0|[1-9]\d*))?(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/

  static tryParse(value: any): Version | undefined {
    if (value instanceof Version) {
      return value
    }

    const match = Version.regExp.exec(value)
    if (!match) return undefined

    const [_, major, minor = "0", patch = "0", prerelease, build] = match

    const coreVersion = new CoreVersion(parseInt(major, 10), parseInt(minor, 10), parseInt(patch, 10))

    return new Version(coreVersion, MetaVersion.parse(prerelease), MetaVersion.parse(build))
  }

  compareTo(other: Version): number {
    return this.coreVersion.compareTo(other.coreVersion) || this.prerelease.compareTo(other.prerelease)
  }

  isEqualTo(other: Version): boolean {
    return this.compareTo(other) === 0
  }

  isGreaterThan(other: Version): boolean {
    return this.compareTo(other) > 0
  }

  isGreaterThanOrEqualTo(other: Version): boolean {
    return this.compareTo(other) >= 0
  }

  isLessThan(other: Version): boolean {
    return this.compareTo(other) < 0
  }

  isLessThanOrEqualTo(other: Version): boolean {
    return this.compareTo(other) <= 0
  }
}

export class CoreVersion {
  readonly major: number
  readonly minor: number
  readonly patch: number

  constructor(major: number, minor: number, patch: number) {
    this.major = major
    this.minor = minor
    this.patch = patch
  }

  compareTo(other: CoreVersion): number {
    return (
      compareValues(this.major, other.major) ||
      compareValues(this.minor, other.minor) ||
      compareValues(this.patch, other.patch)
    )
  }
}

export class MetaVersion {
  readonly identifiers: string[]

  constructor(identifiers: string[]) {
    this.identifiers = identifiers
  }

  private static EMPTY = new MetaVersion([])

  static parse(text: string | undefined): MetaVersion {
    if (!text) {
      return MetaVersion.EMPTY
    } else {
      return new MetaVersion(text.split("."))
    }
  }

  isEmpty(): boolean {
    return this.identifiers.length === 0
  }

  isNotEmpty(): boolean {
    return !this.isEmpty()
  }

  compareTo(other: MetaVersion): number {
    if (this.isEmpty() && other.isEmpty()) {
      return 0
    }

    if (this.isEmpty() && other.isNotEmpty()) {
      return 1
    }

    if (this.isNotEmpty() && other.isEmpty()) {
      return -1
    }

    return this.compareIdentifiers(other)
  }

  private compareIdentifiers(other: MetaVersion): number {
    const length = Math.min(this.identifiers.length, other.identifiers.length)
    for (let i = 0; i < length; i++) {
      const result = MetaVersion.compareIdentifiers(this.identifiers[i], other.identifiers[i])
      if (result !== 0) {
        return result
      }
    }
    return compareValues(this.identifiers.length, other.identifiers.length)
  }

  private static numericIdentifierRegExp = /^(0|[1-9]\d*)$/

  private static compareIdentifiers(identifier1: string, identifier2: string): number {
    if (
      MetaVersion.numericIdentifierRegExp.test(identifier1) &&
      MetaVersion.numericIdentifierRegExp.test(identifier2)
    ) {
      return compareValues(+identifier1, +identifier2)
    }

    if (identifier1 === identifier2) {
      return 0
    }

    return identifier1 < identifier2 ? -1 : 1
  }
}

export type HackleValue = string | number | boolean

export const MATCH_TYPES = ["MATCH", "NOT_MATCH"] as const
export const MATCH_VALUE_TYPES = ["STRING", "NUMBER", "BOOLEAN", "VERSION", "JSON", "URL", "NULL", "UNKNOWN"] as const
export const MATCH_OPERATORS = [
  "IN",
  "CONTAINS",
  "STARTS_WITH",
  "ENDS_WITH",
  "GT",
  "GTE",
  "LT",
  "LTE",
  "EXISTS"
] as const
export const TARGET_ACTION_TYPES = ["VARIATION", "BUCKET"] as const
export const TARGET_KEY_TYPES = [
  "USER_ID",
  "USER_PROPERTY",
  "HACKLE_PROPERTY",
  "SEGMENT",
  "AB_TEST",
  "FEATURE_FLAG",
  "EVENT_PROPERTY",
  "COHORT",
  "NUMBER_OF_EVENTS_IN_DAYS",
  "NUMBER_OF_EVENTS_WITH_PROPERTY_IN_DAYS"
] as const
export const SEGMENT_TYPES = ["USER_ID", "USER_PROPERTY"] as const

export type MatchType = typeof MATCH_TYPES[number]
export type MatchValueType = typeof MATCH_VALUE_TYPES[number]
export type MatchOperator = typeof MATCH_OPERATORS[number]
export type TargetActionType = typeof TARGET_ACTION_TYPES[number]
export type TargetKeyType = typeof TARGET_KEY_TYPES[number]
export type SegmentType = typeof SEGMENT_TYPES[number]

export class TargetingType {
  static IDENTIFIER = new TargetingType("SEGMENT")
  static PROPERTY = new TargetingType(
    "SEGMENT",
    "USER_PROPERTY",
    "HACKLE_PROPERTY",
    "AB_TEST",
    "FEATURE_FLAG",
    "EVENT_PROPERTY",
    "COHORT",
    "NUMBER_OF_EVENTS_IN_DAYS",
    "NUMBER_OF_EVENTS_WITH_PROPERTY_IN_DAYS"
  )
  static SEGMENT = new TargetingType(
    "USER_ID",
    "USER_PROPERTY",
    "HACKLE_PROPERTY",
    "COHORT",
    "NUMBER_OF_EVENTS_IN_DAYS",
    "NUMBER_OF_EVENTS_WITH_PROPERTY_IN_DAYS"
  )

  private supportedKeyTypes: TargetKeyType[]

  constructor(...supportedKeyTypes: TargetKeyType[]) {
    this.supportedKeyTypes = supportedKeyTypes
  }

  supports(keyType: TargetKeyType): boolean {
    return this.supportedKeyTypes.includes(keyType)
  }
}

export const IAM_STATUS = ["ACTIVE", "DRAFT", "PAUSE"] as const
export const IAM_PLATFORM_TYPE = ["WEB", "ANDROID", "IOS"] as const
export const IAM_TIMEUNIT = ["IMMEDIATE", "CUSTOM"] as const
export const IAM_DISPLAY_TYPE = ["HTML", "MODAL", "BANNER", "BOTTOM_SHEET", "NONE"] as const
export const IAM_LAYOUT_TYPE = ["IMAGE_TEXT", "IMAGE_ONLY", "TEXT_ONLY", "IMAGE", "NONE"] as const
export const IAM_ORIENTATION = ["VERTICAL", "HORIZONTAL"] as const
export const IAM_BEHAVIOR = ["CLICK"] as const
export const IAM_ACTION_TYPE = [
  "CLOSE",
  "WEB_LINK",
  "HIDDEN",
  "LINK_AND_CLOSE",
  "LINK_NEW_TAB",
  "LINK_NEW_TAB_AND_CLOSE",
  "LINK_NEW_WINDOW",
  "LINK_NEW_WINDOW_AND_CLOSE"
] as const
export const IAM_VERTICAL_ALIGNMENT = ["TOP", "MIDDLE", "BOTTOM"] as const
export const IAM_HORIZONTAL_ALIGNMENT = ["LEFT", "CENTER", "RIGHT"] as const
export const IAM_ACTION_AREA = ["BUTTON", "IMAGE", "MESSAGE", "X_BUTTON"] as const
export const IAM_TIMETABLE_TYPE = ["ALL", "CUSTOM"] as const
export const IAM_HTML_RESOURCE_TYPE = ["TEXT", "PATH"] as const

export type InAppMessageStatus = typeof IAM_STATUS[number]
export type InAppMessagePlatformType = typeof IAM_PLATFORM_TYPE[number]
export type InAppMessageTimeUnit = typeof IAM_TIMEUNIT[number]
export type InAppMessageDisplayType = typeof IAM_DISPLAY_TYPE[number]
export type InAppMessageLayoutType = typeof IAM_LAYOUT_TYPE[number]
export type InAppMessageOrientation = typeof IAM_ORIENTATION[number]
export type InAppMessageBehavior = typeof IAM_BEHAVIOR[number]
export type InAppMessageActionType = typeof IAM_ACTION_TYPE[number]
export type InAppMessageActionArea = typeof IAM_ACTION_AREA[number]
export type InAppMessageVerticalAlignment = typeof IAM_VERTICAL_ALIGNMENT[number]
export type InAppMessageHorizontalAlignment = typeof IAM_HORIZONTAL_ALIGNMENT[number]
export type InAppMessageTimetableType = typeof IAM_TIMETABLE_TYPE[number]

/**
 * The common identity/metadata contract for an in-app message. Implemented by the local config
 * model (`InAppMessageConfig`) and, in remote evaluation, by the server-resolved results.
 */
export interface InAppMessage extends Entity, HackleInAppMessage {
  order: number
  period: InAppMessagePeriod
  timetable: InAppMessageTimetable
  eventTrigger: InAppMessageEventTrigger
  evaluateContext: InAppMessageEvaluateContext
  messageContext: InAppMessageContext

  supports(platformType: InAppMessagePlatformType): boolean
}

export abstract class AbstractInAppMessage implements InAppMessage {
  abstract readonly id: number
  abstract readonly key: number
  abstract readonly order: number
  abstract readonly period: InAppMessagePeriod
  abstract readonly timetable: InAppMessageTimetable
  abstract readonly eventTrigger: InAppMessageEventTrigger
  abstract readonly messageContext: InAppMessageContext
  abstract readonly evaluateContext: InAppMessageEvaluateContext

  get serviceType(): ServiceType {
    return "IN_APP_MESSAGE"
  }

  public supports(platformType: InAppMessagePlatformType): boolean {
    return this.messageContext.platformTypes.includes(platformType)
  }
}

export class InAppMessageConfig extends AbstractInAppMessage implements ConfigEntity {
  constructor(
    readonly id: number,
    readonly key: number,
    readonly order: number,
    readonly status: InAppMessageStatus,
    readonly period: InAppMessagePeriod,
    readonly timetable: InAppMessageTimetable,
    readonly eventTrigger: InAppMessageEventTrigger,
    readonly targetContext: InAppMessageTargetContext,
    readonly messageContext: InAppMessageContext,
    readonly evaluateContext: InAppMessageEvaluateContext
  ) {
    super()
  }

  static fromJson(dto: InAppMessageDto): InAppMessageConfig | undefined {
    const status = ObjectUtil.parseOrNull(IAM_STATUS, dto.status)
    if (ObjectUtil.isNullOrUndefined(status)) {
      return undefined
    }

    const period = parseInAppMessagePeriodOrNull(dto.timeUnit, dto.startEpochTimeMillis, dto.endEpochTimeMillis)
    if (ObjectUtil.isNullOrUndefined(period)) {
      return undefined
    }

    const timetable = (dto.timetable && InAppMessageTimetable.fromJson(dto.timetable)) ?? new InAppMessageTimetableAll()

    const messageContext = InAppMessageContext.fromJson(dto.messageContext)
    if (ObjectUtil.isNullOrUndefined(messageContext)) {
      return undefined
    }

    const eventTriggerRules = dto.eventTriggerRules.map(InAppMessageEventTriggerRule.fromJson)
    const eventFrequencyCap = InAppMessageEventFrequencyCap.fromJson(dto.eventFrequencyCap)

    const delay = (dto.eventTriggerDelay && Delay.fromJson(dto.eventTriggerDelay)) ?? Delay.immediate()
    const evaluateContext = InAppMessageEvaluateContext.fromJson(dto.evaluateContext ?? { atDeliverTime: false })

    return new InAppMessageConfig(
      dto.id,
      dto.key,
      dto.order,
      status,
      period,
      timetable,
      new InAppMessageEventTrigger(eventTriggerRules, eventFrequencyCap, delay),
      InAppMessageTargetContext.fromJson(dto.targetContext),
      messageContext,
      evaluateContext
    )
  }

  toString(): string {
    return `InAppMessageConfig(id=${this.id}, key=${this.key}, status=${this.status})`
  }
}

export function parseInAppMessagePeriodOrNull(
  timeUnit: string,
  startEpochTimeMillis: number | null,
  endEpochTimeMillis: number | null
): InAppMessagePeriod | undefined {
  switch (ObjectUtil.parseOrNull(IAM_TIMEUNIT, timeUnit)) {
    case "IMMEDIATE":
      return new InAppMessagePeriodAlways()
    case "CUSTOM":
      if (ObjectUtil.isNullOrUndefined(startEpochTimeMillis)) {
        return undefined
      }
      if (ObjectUtil.isNullOrUndefined(endEpochTimeMillis)) {
        return undefined
      }
      return new InAppMessagePeriodCustom(startEpochTimeMillis, endEpochTimeMillis)
    default:
      return undefined
  }
}

export interface InAppMessagePeriod {
  type: InAppMessageTimeUnit
  startMillisInclusive?: number
  endMillisExclusive?: number

  within(timestamp: number): boolean
}

export class InAppMessagePeriodAlways implements InAppMessagePeriod {
  type: InAppMessageTimeUnit = "IMMEDIATE"

  within(timestamp: number): boolean {
    return true
  }
}

export class InAppMessagePeriodCustom implements InAppMessagePeriod {
  type: InAppMessageTimeUnit = "CUSTOM"

  constructor(public readonly startMillisInclusive: number, public readonly endMillisExclusive: number) {}

  within(timestamp: number): boolean {
    return this.startMillisInclusive <= timestamp && timestamp < this.endMillisExclusive
  }
}

export abstract class InAppMessageTimetable {
  abstract type: InAppMessageTimetableType

  abstract within(timestamp: number): boolean

  static fromJson(dto: InAppMessageTimetableDto): InAppMessageTimetable | undefined {
    switch (ObjectUtil.parseOrNull(IAM_TIMETABLE_TYPE, dto.type)) {
      case "ALL":
        return new InAppMessageTimetableAll()
      case "CUSTOM":
        return new InAppMessageTimetableCustom(
          CollectionUtil.mapNotNullOrUndefined(dto.slots, (it) => InAppMessageTimetableSlot.fromJson(it))
        )
      default:
        return undefined
    }
  }
}

export class InAppMessageTimetableAll extends InAppMessageTimetable {
  type: InAppMessageTimetableType = "ALL"

  within(_: number): boolean {
    return true
  }
}

export class InAppMessageTimetableCustom extends InAppMessageTimetable {
  type: InAppMessageTimetableType = "CUSTOM"

  constructor(private readonly slots: InAppMessageTimetableSlot[]) {
    super()
  }

  within(timestamp: number): boolean {
    return this.slots.some((it) => it.within(timestamp))
  }
}

export class InAppMessageTimetableSlot {
  constructor(
    private readonly dayOfWeek: DayOfWeek,
    private readonly startMillisInclusive: number,
    private readonly endMillisExclusive: number
  ) {}

  within(timestamp: number): boolean {
    const dayOfWeek = TimeUtil.dayOfWeek(timestamp)
    if (this.dayOfWeek !== dayOfWeek) {
      return false
    }
    const midnight = TimeUtil.midnight(timestamp)
    const startTimestampInclusive = midnight + this.startMillisInclusive
    const endTimestampExclusive = midnight + this.endMillisExclusive
    return startTimestampInclusive <= timestamp && timestamp < endTimestampExclusive
  }

  static fromJson(dto: InAppMessageTimetableSlotDto): InAppMessageTimetableSlot | undefined {
    const dayOfWeek = ObjectUtil.parseOrNull(DAY_OF_WEEK, dto.dayOfWeek)
    return dayOfWeek && new InAppMessageTimetableSlot(dayOfWeek, dto.startMillisInclusive, dto.endMillisExclusive)
  }
}

export class InAppMessageEventTrigger {
  constructor(
    public readonly rules: InAppMessageEventTriggerRule[],
    public readonly frequencyCap: InAppMessageEventFrequencyCap | null,
    public readonly delay: Delay
  ) {}
}

export class InAppMessageEventTriggerRule {
  constructor(public readonly eventKey: string, public readonly targets: Target[]) {}

  static fromJson(dto: InAppMessageEventTriggerRuleDto): InAppMessageEventTriggerRule {
    return new InAppMessageEventTriggerRule(
      dto.eventKey,
      CollectionUtil.mapNotNullOrUndefined(dto.targets, (it) => Target.fromJson(it, TargetingType.PROPERTY))
    )
  }
}

export class InAppMessageEventFrequencyCap {
  constructor(
    public readonly identifierCaps: InAppMessageIdentifierCap[],
    public readonly durationCap: InAppMessageDurationCap | null
  ) {}

  static fromJson(dto?: InAppMessageEventFrequencyCapDto): InAppMessageEventFrequencyCap | null {
    if (!dto) return null
    const identifierCaps = dto.identifiers.map(InAppMessageIdentifierCap.fromJson)
    const durationCaps = InAppMessageDurationCap.fromJson(dto.duration)
    return new InAppMessageEventFrequencyCap(identifierCaps, durationCaps)
  }
}

export class InAppMessageIdentifierCap {
  constructor(public readonly identifierType: string, public readonly count: number) {}

  static fromJson(dto: InAppMessageIdentifierCapDto): InAppMessageIdentifierCap {
    return new InAppMessageIdentifierCap(dto.identifierType, dto.countPerIdentifier)
  }
}

export class InAppMessageDurationCap {
  constructor(public readonly durationMillis: number, public readonly count: number) {}

  static fromJson(dto?: InAppMessageDurationCapDto): InAppMessageDurationCap | null {
    if (!dto) return null

    const timeUnit = ObjectUtil.parseOrNull(TIME_UNIT, dto.durationUnit.timeUnit)
    if (!timeUnit) return null

    return new InAppMessageDurationCap(
      TimeUtil.converter[timeUnit](dto.durationUnit.amount, "milliseconds"),
      dto.countPerDuration
    )
  }
}

export class InAppMessageTargetContext {
  constructor(readonly overrides: InAppMessageUserOverride[], readonly targets: Target[]) {}

  static fromJson(dto: InAppMessageTargetContextDto): InAppMessageTargetContext {
    return new InAppMessageTargetContext(
      dto.overrides.map(InAppMessageUserOverride.fromJson),
      CollectionUtil.mapNotNullOrUndefined(dto.targets, (it) => Target.fromJson(it, TargetingType.PROPERTY))
    )
  }
}

export class InAppMessageUserOverride {
  constructor(readonly identifierType: string, readonly identifiers: string[]) {}

  static fromJson(dto: InAppMessageTargetRuleOverrideDto): InAppMessageUserOverride {
    return new InAppMessageUserOverride(dto.identifierType, dto.identifiers)
  }
}

export class InAppMessageContext {
  constructor(
    readonly defaultLang: string,
    readonly experimentContext: InAppMessageExperimentContext | null,
    readonly platformTypes: InAppMessagePlatformType[],
    readonly orientations: InAppMessageOrientation[],
    readonly messages: InAppMessageContextMessage[]
  ) {}

  static fromJson(dto: InAppMessageContextDto): InAppMessageContext | undefined {
    let experiment: InAppMessageExperimentContext | null = null
    if (dto.exposure.type === "AB_TEST" && dto.exposure.key !== null) {
      experiment = new InAppMessageExperimentContext(dto.exposure.key)
    }

    const platformTypes = ObjectUtil.parseAllOrNull(IAM_PLATFORM_TYPE, dto.platformTypes)
    if (ObjectUtil.isNullOrUndefined(platformTypes)) {
      return undefined
    }

    const orientations = ObjectUtil.parseAllOrNull(IAM_ORIENTATION, dto.orientations)
    if (ObjectUtil.isNullOrUndefined(orientations)) {
      return undefined
    }

    const messages = CollectionUtil.mapOrUndefined(dto.messages, InAppMessageContextMessage.fromJson)
    if (ObjectUtil.isNullOrUndefined(messages)) {
      return undefined
    }
    return new InAppMessageContext(dto.defaultLang, experiment, platformTypes, orientations, messages)
  }
}

export class InAppMessageExperimentContext {
  constructor(readonly key: number) {}
}

export class InAppMessageContextMessage {
  constructor(
    readonly variationKey: string | null,
    readonly lang: string,
    readonly layout: InAppMessageContextLayout,
    readonly images: InAppMessageImage[],
    readonly imageAutoScroll: InAppMessageImageAutoScroll | undefined,
    readonly text: InAppMessageText | undefined,
    readonly buttons: InAppMessageButton[],
    readonly closeButton: InAppMessageButton | undefined,
    readonly background: InAppMessageBackground,
    readonly action: InAppMessageAction | undefined,
    readonly outerButtons: InAppMessagePositionalButton[],
    readonly innerButtons: InAppMessagePositionalButton[],
    readonly html: InAppMessageHtml | undefined
  ) {}

  static fromJson(dto: InAppMessageContextMessageDto): InAppMessageContextMessage | undefined {
    const layout = InAppMessageContextLayout.fromJson(dto.layout)
    if (ObjectUtil.isNullOrUndefined(layout)) {
      return undefined
    }
    const images = CollectionUtil.mapOrUndefined(dto.images, InAppMessageImage.fromJson)
    if (ObjectUtil.isNullOrUndefined(images)) {
      return undefined
    }

    let imageAutoScroll: InAppMessageImageAutoScroll | undefined = undefined
    if (ObjectUtil.isNotNullOrUndefined(dto.imageAutoScroll)) {
      imageAutoScroll = InAppMessageImageAutoScroll.fromJson(dto.imageAutoScroll)
      if (ObjectUtil.isNullOrUndefined(imageAutoScroll)) {
        return undefined
      }
    }

    const buttons = CollectionUtil.mapOrUndefined(dto.buttons, InAppMessageButton.fromJson)
    if (ObjectUtil.isNullOrUndefined(buttons)) {
      return undefined
    }
    let closeButton: InAppMessageButton | undefined = undefined
    if (ObjectUtil.isNotNullOrUndefined(dto.closeButton)) {
      closeButton = InAppMessageButton.fromJson2(dto.closeButton)
      if (ObjectUtil.isNullOrUndefined(closeButton)) {
        return undefined
      }
    }
    const outerButtons = CollectionUtil.mapOrUndefined(dto.outerButtons, InAppMessagePositionalButton.fromJson)
    const innerButtons = CollectionUtil.mapOrUndefined(dto.innerButtons, InAppMessagePositionalButton.fromJson)

    if (ObjectUtil.isNullOrUndefined(outerButtons) || ObjectUtil.isNullOrUndefined(innerButtons)) {
      return undefined
    }

    let html: InAppMessageHtml | undefined = undefined
    if (ObjectUtil.isNotNullOrUndefined(dto.html)) {
      html = InAppMessageHtmlFactory.fromJson(dto.html)
      if (ObjectUtil.isNullOrUndefined(html)) {
        return undefined
      }
    }

    return new InAppMessageContextMessage(
      dto.variationKey,
      dto.lang,
      layout,
      images,
      imageAutoScroll,
      dto.text ? InAppMessageText.fromJson(dto.text) : undefined,
      buttons,
      closeButton,
      new InAppMessageBackground(dto.background.color),
      dto.action ? InAppMessageAction.fromJson(dto.action) : undefined,
      outerButtons,
      innerButtons,
      html
    )
  }

  public toJson(): InAppMessageContextMessageDto {
    return {
      variationKey: this.variationKey,
      lang: this.lang,
      layout: {
        layoutType: this.layout.layoutType,
        alignment: this.layout.alignment?.toJson() ?? null,
        displayType: this.layout.displayType
      },
      images: this.images.map((it) => it.toJson()),
      imageAutoScroll: this.imageAutoScroll?.toJson() ?? null,
      text: this.text?.toJson() ?? null,
      closeButton: this.closeButton?.toCloseButtonJson() ?? null,
      buttons: this.buttons.map((it) => it.toJson()),
      background: {
        color: this.background.color
      },
      action: this.action?.toJson() ?? null,
      outerButtons: this.outerButtons.map((it) => it.toJson()),
      innerButtons: this.innerButtons.map((it) => it.toJson()),
      html: this.html?.toJson() ?? null
    }
  }
}

export class InAppMessageContextLayout {
  constructor(
    readonly displayType: InAppMessageDisplayType,
    readonly layoutType: InAppMessageLayoutType,
    readonly alignment: InAppMessageAlignment | undefined
  ) {}

  static fromJson(dto: InAppMessageLayoutDto): InAppMessageContextLayout | undefined {
    const layoutType = ObjectUtil.parseOrNull(IAM_LAYOUT_TYPE, dto.layoutType)
    const displayType = ObjectUtil.parseOrNull(IAM_DISPLAY_TYPE, dto.displayType)
    if (ObjectUtil.isNullOrUndefined(displayType)) {
      return undefined
    }
    if (ObjectUtil.isNullOrUndefined(layoutType)) {
      return undefined
    }

    return new InAppMessageContextLayout(
      displayType,
      layoutType,
      dto.alignment ? InAppMessageAlignment.fromJson(dto.alignment) : undefined
    )
  }
}

export class InAppMessageAlignment {
  constructor(readonly horizontal: InAppMessageHorizontalAlignment, readonly vertical: InAppMessageVerticalAlignment) {}

  static fromJson(dto: InAppMessageAlignmentDto): InAppMessageAlignment | undefined {
    const horizontal = ObjectUtil.parseOrNull(IAM_HORIZONTAL_ALIGNMENT, dto.horizontal)
    if (ObjectUtil.isNullOrUndefined(horizontal)) {
      return undefined
    }

    const vertical = ObjectUtil.parseOrNull(IAM_VERTICAL_ALIGNMENT, dto.vertical)
    if (ObjectUtil.isNullOrUndefined(vertical)) {
      return undefined
    }

    return new InAppMessageAlignment(horizontal, vertical)
  }

  toJson(): InAppMessageAlignmentDto {
    return { horizontal: this.horizontal, vertical: this.vertical }
  }
}

export class InAppMessageImage {
  constructor(
    readonly orientation: InAppMessageOrientation,
    readonly imagePath: string,
    readonly action: InAppMessageAction | undefined
  ) {}

  static fromJson(dto: InAppMessageContextImageDto): InAppMessageImage | undefined {
    const orientation = ObjectUtil.parseOrNull(IAM_ORIENTATION, dto.orientation)
    if (ObjectUtil.isNullOrUndefined(orientation)) {
      return undefined
    }
    let action: InAppMessageAction | undefined = undefined
    if (ObjectUtil.isNotNullOrUndefined(dto.action)) {
      action = InAppMessageAction.fromJson(dto.action)
      if (ObjectUtil.isNullOrUndefined(action)) {
        return undefined
      }
    }
    return new InAppMessageImage(orientation, dto.imagePath, action)
  }

  toJson(): InAppMessageContextImageDto {
    return {
      orientation: this.orientation,
      imagePath: this.imagePath,
      action: this.action?.toJson() ?? null
    }
  }
}

export class InAppMessageImageAutoScroll {
  constructor(readonly intervalMillis: number) {}

  static fromJson(dto: InAppMessageContextImageAutoScrollDto): InAppMessageImageAutoScroll | undefined {
    const timeUnit = ObjectUtil.parseOrNull(TIME_UNIT, dto.interval.timeUnit)
    if (!timeUnit) return undefined

    return new InAppMessageImageAutoScroll(TimeUtil.converter[timeUnit](dto.interval.amount, "milliseconds"))
  }

  toJson(): InAppMessageContextImageAutoScrollDto {
    return {
      interval: {
        timeUnit: "milliseconds",
        amount: this.intervalMillis
      }
    }
  }
}

export class InAppMessageText {
  constructor(readonly title: InAppMessageTextAttribute, readonly body: InAppMessageTextAttribute) {}

  static fromJson(dto: InAppMessageContextTextDto): InAppMessageText {
    return new InAppMessageText(
      InAppMessageTextAttribute.fromJson(dto.title),
      InAppMessageTextAttribute.fromJson(dto.body)
    )
  }

  toJson(): InAppMessageContextTextDto {
    return {
      title: {
        text: this.title.text,
        style: {
          textColor: this.title.style.color
        }
      },
      body: {
        text: this.body.text,
        style: {
          textColor: this.body.style.color
        }
      }
    }
  }
}

export class InAppMessageTextAttribute {
  constructor(readonly text: string, readonly style: InAppMessageTextStyle) {}

  static fromJson(dto: InAppMessageContextTextContentDto): InAppMessageTextAttribute {
    return new InAppMessageTextAttribute(dto.text, new InAppMessageTextStyle(dto.style.textColor))
  }
}

export class InAppMessageTextStyle {
  constructor(readonly color: string) {}
}

export class InAppMessageButton {
  constructor(readonly text: string, readonly style: InAppMessageButtonStyle, readonly action: InAppMessageAction) {}

  static fromJson(dto: InAppMessageContextButtonDto): InAppMessageButton | undefined {
    const action = InAppMessageAction.fromJson(dto.action)
    if (ObjectUtil.isNullOrUndefined(action)) {
      return undefined
    }
    return new InAppMessageButton(
      dto.text,
      new InAppMessageButtonStyle(dto.style.textColor, dto.style.bgColor, dto.style.borderColor),
      action
    )
  }

  static fromJson2(dto: InAppMessageContextCloseButtonDto): InAppMessageButton | undefined {
    const action = InAppMessageAction.fromJson(dto.action)
    if (ObjectUtil.isNullOrUndefined(action)) {
      return undefined
    }
    return new InAppMessageButton("✕", new InAppMessageButtonStyle(dto.style.color, "#FFFFFF", "#FFFFFF"), action)
  }

  toJson(): InAppMessageContextButtonDto {
    return {
      text: this.text,
      action: this.action.toJson(),
      style: {
        textColor: this.style.textColor,
        bgColor: this.style.backgroundColor,
        borderColor: this.style.borderColor
      }
    }
  }

  toCloseButtonJson(): InAppMessageContextCloseButtonDto {
    return {
      action: this.action.toJson(),
      style: {
        color: this.style.textColor
      }
    }
  }
}

export class InAppMessagePositionalButton {
  constructor(readonly button: InAppMessageButton, readonly alignment: InAppMessageAlignment) {}

  static fromJson(dto: InAppMessagePositionalButtonDto): InAppMessagePositionalButton | undefined {
    const button = InAppMessageButton.fromJson(dto.button)
    if (ObjectUtil.isNullOrUndefined(button)) {
      return undefined
    }

    const alignment = InAppMessageAlignment.fromJson(dto.alignment)
    if (ObjectUtil.isNullOrUndefined(alignment)) {
      return undefined
    }

    return new InAppMessagePositionalButton(button, alignment)
  }

  toJson(): InAppMessagePositionalButtonDto {
    return { button: this.button.toJson(), alignment: this.alignment.toJson() }
  }
}

export class InAppMessageButtonStyle {
  constructor(readonly textColor: string, readonly backgroundColor: string, readonly borderColor: string) {}
}

export class InAppMessageBackground {
  constructor(readonly color: string) {}
}

export class InAppMessageAction {
  private static readonly DEFAULT_HIDE_DURATION_MILLIS = IAM_HIDE_EXPIRATION_MILLIS

  constructor(
    readonly behavior: InAppMessageBehavior,
    readonly type: InAppMessageActionType,
    readonly value: string | undefined
  ) {}

  get hideDurationMillis(): number | null {
    if (this.type !== "HIDDEN") return null

    if (ObjectUtil.isNullOrUndefined(this.value) || this.value.trim() === "") {
      return InAppMessageAction.DEFAULT_HIDE_DURATION_MILLIS
    }
    const millis = TypeUtil.asNumber(this.value)
    return ObjectUtil.isNullOrUndefined(millis) ? InAppMessageAction.DEFAULT_HIDE_DURATION_MILLIS : millis
  }

  static fromJson(dto: InAppMessageContextActionDto): InAppMessageAction | undefined {
    const behavior = ObjectUtil.parseOrNull(IAM_BEHAVIOR, dto.behavior)
    if (ObjectUtil.isNullOrUndefined(behavior)) {
      return undefined
    }
    const type = ObjectUtil.parseOrNull(IAM_ACTION_TYPE, dto.type)
    if (ObjectUtil.isNullOrUndefined(type)) {
      return undefined
    }
    return new InAppMessageAction(behavior, type, dto.value ?? undefined)
  }

  toJson(): InAppMessageContextActionDto {
    return {
      behavior: this.behavior,
      type: this.type,
      value: this.value ?? null
    }
  }
}

export class InAppMessageEvaluateContext {
  constructor(readonly atDeliverTime: boolean) {}

  static fromJson(dto: InAppMessageEvaluateContextDto): InAppMessageEvaluateContext {
    return new InAppMessageEvaluateContext(dto.atDeliverTime)
  }
}

export class InAppMessageAfterCondition {
  constructor(readonly durationMillis: number) {}

  static fromJson(dto: InAppMessageAfterConditionDto): InAppMessageAfterCondition | null {
    const timeUnit = ObjectUtil.parseOrNull(TIME_UNIT, dto.duration.timeUnit)
    if (ObjectUtil.isNullOrUndefined(timeUnit)) {
      return null
    }

    const durationMillis = TimeUtil.converter[timeUnit](dto.duration.amount, "milliseconds")
    return new InAppMessageAfterCondition(durationMillis)
  }
}

export class Delay {
  constructor(readonly type: "IMMEDIATE" | "AFTER", readonly afterCondition: InAppMessageAfterCondition | null) {}

  static fromJson(dto: InAppMessageDelayDto): Delay {
    const afterCondition = dto.afterCondition && InAppMessageAfterCondition.fromJson(dto.afterCondition)

    return new Delay(dto.type, afterCondition)
  }

  static immediate(): Delay {
    return new Delay("IMMEDIATE", null)
  }

  deliverAt(startedAt: number): number {
    const delayType = this.type
    switch (delayType) {
      case "IMMEDIATE":
        return startedAt
      case "AFTER":
        if (this.afterCondition === null) {
          throw new Error("AfterCondition is required for AFTER delay type")
        }
        return startedAt + this.afterCondition.durationMillis
      default:
        throw new Error(`Unsupported delay type: ${delayType}`)
    }
  }
}

export type InAppMessageHtml = InAppMessageHtmlText | InAppMessageHtmlPath

export class InAppMessageHtmlText {
  readonly resourceType = "TEXT"
  readonly path = null

  constructor(readonly text: string) {}

  toJson(): InAppMessageHtmlDto {
    return { resourceType: "TEXT", text: this.text, path: null }
  }
}

export class InAppMessageHtmlPath {
  readonly resourceType = "PATH"
  readonly text = null

  constructor(readonly path: string) {}

  toJson(): InAppMessageHtmlDto {
    return { resourceType: "PATH", text: null, path: this.path }
  }
}

export class InAppMessageHtmlFactory {
  static fromJson(dto: InAppMessageHtmlDto): InAppMessageHtml | undefined {
    const resourceType = ObjectUtil.parseOrNull(IAM_HTML_RESOURCE_TYPE, dto.resourceType)
    if (ObjectUtil.isNullOrUndefined(resourceType)) {
      return undefined
    }

    switch (resourceType) {
      case "TEXT":
        if (ObjectUtil.isNullOrUndefined(dto.text)) return undefined
        return new InAppMessageHtmlText(dto.text)
      case "PATH":
        if (ObjectUtil.isNullOrUndefined(dto.path)) return undefined
        return new InAppMessageHtmlPath(dto.path)
      default:
        return undefined
    }
  }
}

export class Cohort {
  constructor(readonly id: number) {}
}

export interface Sdk {
  readonly key: string
  readonly name: string
  readonly version: string
}

// Split URL
export enum SplitUrlRedirectType {
  MANUAL = "MANUAL"
}

const isSplitUrlRedirectType = (value: unknown): value is SplitUrlRedirectType =>
  Object.values(SplitUrlRedirectType).includes(value as any)

export class SplitUrlConfig {
  constructor(
    readonly needRedirect: boolean,
    readonly redirectType: SplitUrlRedirectType | null,
    readonly redirectUrl: string | null
  ) {}

  static fromDto(dto: SplitUrlConfigDto): SplitUrlConfig {
    const needRedirect = dto.needRedirect
    const redirectType = isSplitUrlRedirectType(dto.properties.type) ? dto.properties.type : null
    const redirectUrl = dto.properties.value

    return new SplitUrlConfig(needRedirect, redirectType, redirectUrl)
  }
}

export class SplitUrlTargets {
  public static fromJson(dto: ExecutionTargetUrlsDto | null): SplitUrlTargets | undefined {
    if (dto === null) {
      return undefined
    }

    const includeTargets = CollectionUtil.mapNotNullOrUndefined(dto.match, (it) =>
      Target.fromJson(it, TargetingType.PROPERTY)
    )
    const excludeTargets = CollectionUtil.mapNotNullOrUndefined(dto.notMatch, (it) =>
      Target.fromJson(it, TargetingType.PROPERTY)
    )

    return new SplitUrlTargets(includeTargets, excludeTargets)
  }

  constructor(readonly includeTargets: Target[], readonly excludeTargets: Target[]) {}
}

export class UserTargetEvent {
  constructor(
    readonly eventKey: string,
    readonly property: UserTargetEventPropertyDto | null,
    readonly stats: UserTargetStatDto[]
  ) {}

  static fromJson(dto: UserTargetEventDto): UserTargetEvent {
    const { eventKey, property, stats } = dto

    return new UserTargetEvent(eventKey, property, stats)
  }

  countWithinDays(daysAgoUtc: number): number {
    return CollectionUtil.sum(
      this.stats.filter((it) => it.date > daysAgoUtc),
      (it) => it.count
    )
  }
}

export type UserTargetEventSupportedTargetKey = "NUMBER_OF_EVENTS_IN_DAYS" | "NUMBER_OF_EVENTS_WITH_PROPERTY_IN_DAYS"

export interface NumberOfEventsInDaysExpression {
  eventKey: string
  days: number
}

export interface NumberOfEventsWithPropertyInDaysExpression {
  eventKey: string
  days: number
  propertyFilter: TargetCondition
}

export type TargetSegmentationExpression = NumberOfEventsInDaysExpression | NumberOfEventsWithPropertyInDaysExpression
